class StockistQuoteBuilder {
  constructor(root) {
    this.root = root;
    this.storageKey = 'wiggly-stockist-quote-v1';
    this.products = new Map();
    this.selection = new Map();
    this.intent = '';
    this.catalogueStep = window.matchMedia('(max-width: 767px)').matches ? 12 : 18;
    this.catalogueLimit = this.catalogueStep;
    this._collectProducts();
    this._restore();
    this._buildFilters();
    this._bind();
    this._setMinimumDate();
    this._render();
  }

  _collectProducts() {
    this.root.querySelectorAll('[data-quote-product]').forEach((card) => {
      const product = {
        id: card.dataset.productId,
        title: card.dataset.productTitle,
        category: card.dataset.productCategory || 'Other',
        image: card.dataset.productImage,
        url: card.dataset.productUrl,
        card,
      };
      this.products.set(product.id, product);
    });
  }

  _restore() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
      this.intent = typeof stored.intent === 'string' ? stored.intent : '';
      Object.entries(stored.items || {}).forEach(([id, qty]) => {
        if (this.products.has(id) && Number.isFinite(Number(qty)) && Number(qty) > 0) {
          this.selection.set(id, Math.min(999, Math.round(Number(qty))));
        }
      });
    } catch (_error) {
      try { sessionStorage.removeItem(this.storageKey); } catch (_storageError) { /* Storage is optional. */ }
    }
  }

  _persist() {
    const items = {};
    this.selection.forEach((qty, id) => { items[id] = qty; });
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ intent: this.intent, items }));
    } catch (_error) {
      /* The builder still works when browser storage is unavailable. */
    }
  }

  _buildFilters() {
    const select = this.root.querySelector('[data-quote-filter]');
    if (!select) return;
    const categories = [...new Set([...this.products.values()].map((product) => product.category).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    categories.forEach((category) => {
      const option = document.createElement('option');
      option.value = category.toLowerCase();
      option.textContent = category;
      select.appendChild(option);
    });
  }

  _bind() {
    this.root.querySelectorAll('input[name="quote-intent-choice"]').forEach((input) => {
      if (input.value === this.intent) input.checked = true;
      input.addEventListener('change', () => {
        this.intent = input.value;
        this._persist();
        const error = this.root.querySelector('[data-quote-selection-error]');
        if (error && this.selection.size) error.hidden = true;
        this._render();
      });
    });

    this.products.forEach((product) => {
      product.card.querySelector('[data-quote-add]')?.addEventListener('click', () => {
        this.selection.set(product.id, 1);
        this._announce(`${product.title} added to your shortlist.`);
        this._changed();
      });
      product.card.querySelector('[data-quote-increase]')?.addEventListener('click', () => {
        this.selection.set(product.id, Math.min(999, (this.selection.get(product.id) || 0) + 1));
        this._changed();
      });
      product.card.querySelector('[data-quote-decrease]')?.addEventListener('click', () => {
        const next = (this.selection.get(product.id) || 0) - 1;
        if (next <= 0) {
          this.selection.delete(product.id);
          this._announce(`${product.title} removed from your shortlist.`);
        } else {
          this.selection.set(product.id, next);
        }
        this._changed();
      });
    });

    this.root.querySelector('[data-quote-search]')?.addEventListener('input', () => {
      this.catalogueLimit = this.catalogueStep;
      this._filter();
    });
    this.root.querySelector('[data-quote-filter]')?.addEventListener('change', () => {
      this.catalogueLimit = this.catalogueStep;
      this._filter();
    });
    this.root.querySelector('[data-quote-more]')?.addEventListener('click', () => {
      this.catalogueLimit += this.catalogueStep;
      this._filter();
    });

    this.root.querySelectorAll('[data-quote-continue], [data-quote-mobile-continue]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!this.selection.size || !this.intent) {
          event.preventDefault();
          this._showSelectionError();
        }
      });
    });

    const form = this.root.querySelector('.stockist-quote-form');
    form?.addEventListener('submit', (event) => {
      if (!this.selection.size || !this.intent) {
        event.preventDefault();
        this._showSelectionError();
        return;
      }
      this._syncFields();
    });

    if (this.root.querySelector('[data-quote-success]')) {
      try { sessionStorage.removeItem(this.storageKey); } catch (_error) { /* Storage is optional. */ }
      this.selection.clear();
      this.intent = '';
    }
  }

  _setMinimumDate() {
    const date = this.root.querySelector('[data-quote-required-date]');
    if (!date) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    date.min = tomorrow.toISOString().slice(0, 10);
  }

  _changed() {
    this._persist();
    this._render();
  }

  _announce(message) {
    const live = this.root.querySelector('[data-quote-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 30);
  }

  _filter() {
    const query = (this.root.querySelector('[data-quote-search]')?.value || '').trim().toLowerCase();
    const category = this.root.querySelector('[data-quote-filter]')?.value || '';
    let visible = 0;
    let matching = 0;
    this.products.forEach((product) => {
      const matchesQuery = !query || product.title.toLowerCase().includes(query);
      const matchesCategory = !category || product.category.toLowerCase() === category;
      const matches = matchesQuery && matchesCategory;
      if (matches) matching += 1;
      const show = matches && matching <= this.catalogueLimit;
      product.card.hidden = !show;
      if (show) visible += 1;
    });
    const empty = this.root.querySelector('[data-quote-empty]');
    if (empty) empty.hidden = matching !== 0;
    const moreWrap = this.root.querySelector('[data-quote-more-wrap]');
    const more = this.root.querySelector('[data-quote-more]');
    if (moreWrap) moreWrap.hidden = matching === 0;
    if (more) more.hidden = visible >= matching;
    const results = this.root.querySelector('[data-quote-results]');
    if (results) {
      const template = this.root.dataset.resultsTemplate || 'Showing [visible] of [total] products';
      results.textContent = template.replace('[visible]', visible).replace('[total]', matching);
    }
  }

  _render() {
    this._renderIntent();
    this._renderCards();
    this._renderSummaries();
    this._syncFields();
    this._filter();
  }

  _renderIntent() {
    this.root.querySelectorAll('.stockist-intent-card').forEach((label) => {
      label.classList.toggle('is-selected', Boolean(label.querySelector('input')?.checked));
    });
    const field = this.root.querySelector('[data-quote-intent-field]');
    if (field) field.value = this.intent;
  }

  _renderCards() {
    this.products.forEach((product) => {
      const qty = this.selection.get(product.id) || 0;
      product.card.classList.toggle('is-selected', qty > 0);
      const add = product.card.querySelector('[data-quote-add]');
      const controls = product.card.querySelector('[data-quote-quantity]');
      if (add) add.hidden = qty > 0;
      if (controls) controls.hidden = qty === 0;
      const output = product.card.querySelector('[data-quote-quantity-value]');
      if (output) output.textContent = qty || 1;
    });
  }

  _selectionRows() {
    return [...this.selection.entries()].map(([id, qty]) => ({ ...this.products.get(id), qty }));
  }

  _totalUnits() {
    return this._selectionRows().reduce((sum, product) => sum + product.qty, 0);
  }

  _summaryItem(product, compact = false) {
    const li = document.createElement('li');
    li.className = compact ? 'stockist-summary-item stockist-summary-item--compact' : 'stockist-summary-item';

    const img = document.createElement('img');
    img.src = product.image;
    img.alt = '';
    img.width = 52;
    img.height = 52;
    img.loading = 'lazy';

    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = product.title;
    const quantity = document.createElement('small');
    quantity.textContent = `Approx. ${product.qty}`;
    copy.append(title, quantity);

    li.append(img, copy);

    if (!compact) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${product.title}`);
      remove.addEventListener('click', () => {
        this.selection.delete(product.id);
        this._announce(`${product.title} removed from your shortlist.`);
        this._changed();
      });
      li.append(remove);
    }
    return li;
  }

  _renderSummaries() {
    const rows = this._selectionRows();
    const total = this._totalUnits();
    const desktop = this.root.querySelector('[data-quote-summary-items]');
    const mobile = this.root.querySelector('[data-quote-mobile-items]');
    if (desktop) desktop.replaceChildren(...rows.map((product) => this._summaryItem(product)));
    if (mobile) mobile.replaceChildren(...rows.map((product) => this._summaryItem(product, true)));

    const empty = this.root.querySelector('[data-quote-summary-empty]');
    if (empty) empty.hidden = rows.length > 0;
    const totalRow = this.root.querySelector('[data-quote-summary-total]');
    if (totalRow) totalRow.hidden = rows.length === 0;
    const totalEl = this.root.querySelector('[data-quote-total-units]');
    if (totalEl) totalEl.textContent = total;
    const mobileCount = this.root.querySelector('[data-quote-mobile-count]');
    if (mobileCount) mobileCount.textContent = `${total} ${total === 1 ? 'unit' : 'units'}`;

    this.root.querySelectorAll('[data-quote-continue], [data-quote-mobile-continue]').forEach((link) => {
      const disabled = rows.length === 0 || !this.intent;
      link.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      link.classList.toggle('is-disabled', disabled);
      if (!this.intent && rows.length === 0) {
        link.textContent = this.root.dataset.continueNeedsBoth || 'Choose a type and products';
      } else if (!this.intent) {
        link.textContent = this.root.dataset.continueNeedsIntent || 'Choose an order type';
      } else if (rows.length === 0) {
        link.textContent = this.root.dataset.continueNeedsProducts || 'Add products to continue';
      } else {
        link.textContent = this.root.dataset.continueReady || 'Continue to details';
      }
    });

    const mobileTray = this.root.querySelector('[data-quote-mobile-summary]');
    if (mobileTray) mobileTray.hidden = rows.length === 0;
  }

  _syncFields() {
    const rows = this._selectionRows();
    const shortlist = rows.map((product) => `${product.title} — approximately ${product.qty}`).join('\n');
    const shortlistField = this.root.querySelector('[data-quote-shortlist-field]');
    const totalField = this.root.querySelector('[data-quote-total-field]');
    const intentField = this.root.querySelector('[data-quote-intent-field]');
    if (shortlistField) shortlistField.value = shortlist;
    if (totalField) totalField.value = String(this._totalUnits());
    if (intentField) intentField.value = this.intent;
  }

  _showSelectionError() {
    const error = this.root.querySelector('[data-quote-selection-error]');
    if (error) error.hidden = false;
    const target = this.intent ? this.root.querySelector('[data-quote-builder]') : this.root.querySelector('[data-quote-intents]');
    target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const focusTarget = this.intent
      ? this.root.querySelector('[data-quote-search]')
      : this.root.querySelector('input[name="quote-intent-choice"]');
    focusTarget?.focus({ preventScroll: true });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-stockist-quote]').forEach((root) => new StockistQuoteBuilder(root));
});
